import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  ConfirmationDialog,
  FilterToolbarSearch,
  filterToolbarSearchGrowClassName,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsCount,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Download,
  HelpCircle,
  Loader2,
  Undo2,
  Upload,
} from 'lucide-react';
import { parseAsStringLiteral, useQueryState, useQueryStates } from 'nuqs';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  downloadShpSentXml,
  exportOasisBatch,
  previewShpBatch,
  listOasisManagerVisits,
  markVisitsExported,
  precheckOasisBatch,
  unmarkVisitsExported,
} from '../services/api';
import type {
  OasisExportReadiness,
  OasisManagerFilterOptions,
  OasisManagerVisitItem,
  VisitTypeId,
  OasisValidationError,
} from '../services/api/types.gen';
import type { SurfaceableError } from '../utils/apiErrorDetail';
import {
  EMPTY_OASIS_MANAGER_COLUMN_FILTERS,
  type OASISManagerColumnFilters,
  type OASISManagerFilters,
} from '../store/slices/oasisManagerSlice';
import type { ColumnDef } from '@tanstack/react-table';
import {
  type HeaderFilterConfig,
  Spreadsheet,
} from '../components/spreadsheet';
import {
  formatDateTimeInAgencyZone,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToastContext';
import { getVisitTypeDisplayName } from '../hooks/useVisitTypeOptions';
import {
  getResponseDetailFromBlobOrJson,
  getSurfaceableError,
  getUserFacingApiMessage,
} from '../utils/apiErrorDetail';
import { getHttpStatus } from '../utils/httpErrorUtils';
import {
  downloadBlobAsFile,
  downloadResponseAsFile,
} from '../utils/downloadFile';
import { OpenOasisReportWithQueryState } from './OpenOasisReport';
import { getOpenOasisReportReportsOpenOasisGet } from '../services/api/sdk.gen';
import { formatTabCount } from '../components/billing/formatTabCount';
import { ExcludePreTakeoverFilterCheckbox } from '../components/filters/ExcludePreTakeoverFilterCheckbox';
import { ImportedPatientBadge } from '../components/patient/ImportedPatientBadge';
import { ManagedInExternalEmrMark } from '../components/task/ManagedInExternalEmrMark';
import {
  EXTERNAL_MANAGED_ROW_CLASS,
  taskShowsPreTakeOverHatch,
} from '../components/patient/externalTaskRows';
import {
  externalEmrOasisWarning,
  externalEmrOwned,
  type ExternalEmrOasisAction,
} from '../components/oasis/externalEmrOasisWarning';
import { OasisExportErrorsDialog } from '../components/oasis/OasisExportErrorsDialog';
import {
  chartFixTargetForError,
  chartFixUrl,
  clinicSettingsUrl,
  fieldForCmsItem,
  cmsItemHome,
  patientInfoUrl,
} from '../components/oasis/cmsFixTarget';
import { SHP_UNSENDABLE_REASONS } from '../components/oasis/cmsFieldErrors';
import { useOasisExportReadiness } from '../hooks/useOasisExportReadiness';
import { useShpOasisSubmissionEnabled } from '../hooks/useShpOasisSubmissionEnabled';
import { useCanRunShpCheck } from '../hooks/useShpCheck';
import { ShpCheckControl } from '../components/oasis/ShpCheckControl';
import { TestPatientBadge } from '../components/patient/TestPatientBadge';
import { ReportLink } from '../components/reporting/ReportLink';
import {
  ContactSupportLink,
  SupportMessage,
} from '../components/support/ContactSupportLink';
import { PermissionSlug } from '../constants/featureFlags';
import type { ColumnSort } from '../components/SortableTableHead';

/** The ticked values of the filters the server lists, or null when all are still offered. */
function offeredColumnFilters(
  filters: OASISManagerColumnFilters,
  options: OasisManagerFilterOptions | null,
  // The SHP Status values the tab offers, or null while SHP is off.
  shpStatuses: readonly string[] | null
): OASISManagerColumnFilters | null {
  const offered = {
    visitTypes: new Set<string>(options?.visit_types ?? []),
    clinicians: new Set<string>([
      ...(options?.has_unassigned_clinician ? ['unassigned'] : []),
      ...(options?.clinicians ?? []).map(o => o.value),
    ]),
    clinics: new Set<string>([
      ...(options?.has_no_clinic ? ['none'] : []),
      ...(options?.clinics ?? []).map(o => o.value),
    ]),
  };
  const kept = {
    ...filters,
    visitTypes: filters.visitTypes.filter(v => offered.visitTypes.has(v)),
    clinicians: filters.clinicians.filter(v => offered.clinicians.has(v)),
    clinics: filters.clinics.filter(v => offered.clinics.has(v)),
    shpStatuses: shpStatuses
      ? filters.shpStatuses.filter(v => shpStatuses.includes(v))
      : filters.shpStatuses,
  };
  const changed =
    kept.visitTypes.length !== filters.visitTypes.length ||
    kept.clinicians.length !== filters.clinicians.length ||
    kept.clinics.length !== filters.clinics.length ||
    kept.shpStatuses.length !== filters.shpStatuses.length;
  return changed ? kept : null;
}

const STATUS_BY_TAB: Record<
  Exclude<OASISManagerFilters['activeTab'], 'incomplete'>,
  string
> = {
  ready_to_export: 'completed',
  exported: 'exported',
};

// A cell's content box: the grid's cells carry no padding of their own.
const OASIS_CELL_CLASS = 'flex min-h-9 items-center gap-2 px-2 py-1';

// Zero-padded CMS correction numbers. 00 = new submission; 01, 02, … are
// resubmissions of an already-accepted OASIS. The backend accepts 00–99; the
// dropdown is capped at 10, which covers realistic resubmission counts.
const CORRECTION_NUM_OPTIONS = Array.from({ length: 11 }, (_, i) =>
  String(i).padStart(2, '0')
);
const DEFAULT_CORRECTION_NUM = '00';

// CMS transaction type follows from the correction number: a new submission
// (00) is trans_type 1; any correction is a modification (trans_type 2).
function transTypeForCorrectionNum(correctionNum: string): '1' | '2' {
  return correctionNum === DEFAULT_CORRECTION_NUM ? '1' : '2';
}

function daysUntilDeadline(visitDate: string): number {
  const d = parseISOToLocalDate(visitDate);
  if (!d) return 0;
  const deadlineUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 30);
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((deadlineUtc - todayUtc) / (1000 * 60 * 60 * 24));
}

// Columns the server sorts by (`sort_by` on GET /visits/oasis-manager).
const OASIS_SORT_COLUMNS = [
  'patient_name',
  'visit_type',
  'visit_date',
  'status',
  'clinic',
  'clinician',
  'exported_at',
  'exported_by',
  'shp_sent_at',
  // Held first, then due on the next run, never sent, and sent (newest first).
  'shp_status',
] as const;
type OasisSortColumn = (typeof OASIS_SORT_COLUMNS)[number];

// Distinct from the Incomplete tab report's `sort`/`dir` params.
const oasisSortParsers = {
  sort_by: parseAsStringLiteral(OASIS_SORT_COLUMNS),
  sort_order: parseAsStringLiteral(['asc', 'desc'] as const).withDefault('asc'),
};

const OASIS_MANAGER_TAB_VALUES = [
  'incomplete',
  'ready_to_export',
  'exported',
] as const;

const OASIS_TABS: {
  value: (typeof OASIS_MANAGER_TAB_VALUES)[number];
  label: string;
}[] = [
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'ready_to_export', label: 'Ready to Export' },
  { value: 'exported', label: 'Exported' },
];

type OasisTabCounts = Record<
  (typeof OASIS_MANAGER_TAB_VALUES)[number],
  number | null
>;

/**
 * The page's one blocking-error surface: a dismissible destructive Alert.
 *
 * Every export failure renders through this rather than a toast — a toast is
 * string-only, so it can't carry the "Contact Support" escape hatch, and it
 * disappears before the user can act on it.
 */
const DismissibleExportError: React.FC<{
  title: string;
  onDismiss: () => void;
  children: React.ReactNode;
}> = ({ title, onDismiss, children }) => (
  <Alert variant='destructive' className='mb-4 flex items-start gap-3'>
    <div className='flex-1'>
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className='text-muted-foreground'>
        {children}
      </AlertDescription>
    </div>
    <Button size='sm' variant='ghost' onClick={onDismiss}>
      Dismiss
    </Button>
  </Alert>
);

/**
 * A failed export, ready to render. `body` overrides the rendered message when
 * it needs markup (a link to the fix); `detail` overrides what support is told
 * when the user-facing copy is too generic to act on. Both default to
 * `message`, which is the common case.
 */
type ExportError = {
  title: string;
  message: string;
  body?: React.ReactNode;
  detail?: string;
  /** The assessments this failure is about, captured when it happened. The
   * copy tells the user to uncheck the failing rows, so reading the live
   * selection later would report whatever survived that edit — or nothing. */
  visitIds: string[];
  /** Why each one failed, listed under the message (EMR-2340). */
  assessments?: FailedAssessment[];
};

type FailedAssessment = {
  visitId: string | null;
  patientName: string | null;
  /** `item` is the CMS item, which says where the fix is; `label` is shown. */
  reasons: { label: string; item: string }[];
};

const EXPORT_FAILED_TITLE = 'Export failed';

/**
 * The visit ids CMS (iQIES) named in a VUT rejection. Shape is documented on
 * the backend surfaceable-error contract; anything unexpected returns null so
 * the caller falls back to what it actually sent.
 */
function rejectedVisitIds(data: Record<string, unknown>): string[] | null {
  const assessments = (data as { assessments?: unknown }).assessments;
  if (!Array.isArray(assessments)) return null;
  const ids = assessments
    .map(a => (a as { visit_id?: unknown })?.visit_id)
    .filter((id): id is string => typeof id === 'string');
  return ids.length > 0 ? ids : null;
}

/**
 * The items CMS (iQIES) objected to on each assessment it rejected, as
 * "M0065: Inconsistent M0150/M0065 Values." A scrubber refusal has none: its
 * errors show as the All CMS tab's cards in the row's dialog instead.
 * Anything unexpected is dropped rather than rendered raw.
 */
function failedAssessments(data: Record<string, unknown>): FailedAssessment[] {
  const assessments = (data as { assessments?: unknown }).assessments;
  if (!Array.isArray(assessments)) return [];
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return assessments.map(raw => {
    const a = raw as {
      visit_id?: unknown;
      patient_name?: unknown;
      errors?: unknown;
    };
    const errors = Array.isArray(a.errors) ? a.errors : [];
    return {
      visitId: typeof a.visit_id === 'string' ? a.visit_id : null,
      patientName: typeof a.patient_name === 'string' ? a.patient_name : null,
      reasons: errors
        .map(e => {
          const err = e as Record<string, unknown>;
          // "M0065_MEDICAID_NUM" reads as "M0065"; non-question items stay whole.
          const item = str(err.item);
          const code = item.match(/^[A-Z]{1,2}\d{4}[A-Z0-9]*/)?.[0] ?? item;
          const text = str(err.text);
          const label = code && text ? `${code}: ${text}` : code || text;
          return { label, item };
        })
        .filter(reason => reason.label),
    };
  });
}

/** Rows the CMS (iQIES) export can send: never test patients, nor visit types
 * CMS does not accept. */
const isCmsExportable = (v: OasisManagerVisitItem) =>
  !v.is_test_patient && v.is_cms_submittable !== false;

const NO_NIGHTLY_RUN = 'No nightly run on this environment';

/** The SHP Status filter's choices; Exported charts are never held. */
const SHP_STATUS_FILTER_OPTIONS = [
  { value: 'held', label: 'Held' },
  { value: 'due', label: 'Goes to SHP next run' },
  { value: 'unsendable', label: 'Not sent to SHP' },
  { value: 'sent', label: 'Sent' },
] as const;
type ShpStatusFilter = (typeof SHP_STATUS_FILTER_OPTIONS)[number]['value'];
const SHP_STATUS_FILTER_CHOICES: { value: ShpStatusFilter; label: string }[] =
  SHP_STATUS_FILTER_OPTIONS.map(o => ({ value: o.value, label: o.label }));

/** Charts the nightly SHP job looks at next; only these can be held. */
const isShpDue = (v: OasisManagerVisitItem) =>
  v.shp_due === true && !v.shp_unsendable_reason;

/** The nightly run found a record SHP could not file. CMS errors hold nothing
 * back: the patient name's error mark reports those. */
const hasRecordProblems = (v: OasisManagerVisitItem) =>
  (v.shp_hold_problems?.length ?? 0) > 0;

export const SHP_HELD_LABEL = "Held: SHP can't file it";

/** What the "SHP Status" cell says, by the nightly job's own rules. */
function shpStatus(
  visit: OasisManagerVisitItem,
  nextRunAt: string | null
): { label: string; detail?: string } {
  const sentAt = formatDateTimeInAgencyZone(visit.shp_last_submitted_at);
  const reason = visit.shp_unsendable_reason;
  if (reason) {
    const why = SHP_UNSENDABLE_REASONS[reason];
    return sentAt
      ? { label: sentAt, detail: `Not sent again: ${why}` }
      : { label: 'Not sent to SHP', detail: why };
  }
  if (isShpDue(visit) && hasRecordProblems(visit)) {
    return {
      label: SHP_HELD_LABEL,
      detail: sentAt ? `Last sent ${sentAt}` : undefined,
    };
  }
  // Null where this environment has no nightly run to name.
  const nextRun = formatDateTimeInAgencyZone(nextRunAt);
  if (!sentAt) {
    if (!visit.shp_due) return { label: 'Not yet' };
    return nextRun
      ? { label: `Goes to SHP ${nextRun}` }
      : { label: 'Not yet', detail: NO_NIGHTLY_RUN };
  }
  const details = [
    visit.shp_sent_to_test_provider && 'Sent to SHP test',
    visit.shp_due &&
      (nextRun ? `Checked again ${nextRun}` : 'Changed since sent'),
    visit.shp_due && !nextRun && NO_NIGHTLY_RUN,
  ].filter(Boolean);
  return { label: sentAt, detail: details.join(' · ') || undefined };
}

/** The two refusals that name the assessments they are about. */
const REFUSAL_TITLES: Record<string, string> = {
  oasis_cms_fatal_errors: 'Some charts have errors CMS will reject',
  oasis_vut_validation_failed: 'CMS rejected some assessments',
};

const OASISManager: React.FC = () => {
  const {
    oasisManagerFilters,
    setOASISManagerFilters,
    hasAccess,
    canEditClinics,
    canEditPatientInfo,
    isSuperUser,
  } = useStore(
    useShallow(state => ({
      oasisManagerFilters: state.oasisManagerFilters,
      setOASISManagerFilters: state.setOASISManagerFilters,
      hasAccess: state.hasPermission(PermissionSlug.OASIS_MANAGER),
      canEditClinics: state.hasPermission(PermissionSlug.CLINIC_INFO_EDIT),
      canEditPatientInfo: state.hasPermission(PermissionSlug.PATIENT_INFO_EDIT),
      isSuperUser: state.hasPermission(PermissionSlug.ADMIN_SUPER_USER),
    }))
  );

  const [visits, setVisits] = useState<OasisManagerVisitItem[]>([]);
  const [total, setTotal] = useState(0);
  const [tabCounts, setTabCounts] = useState<OasisTabCounts | null>(null);
  const tabCountsGenerationRef = useRef(0);
  const [shpNextRunAt, setShpNextRunAt] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] =
    useState<OasisManagerFilterOptions | null>(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // Whether the list on screen answered a column filter. Clearing the last
  // value keeps its headers and open checklist until the unfiltered list lands.
  const [listWasFiltered, setListWasFiltered] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Per-visit correction number chosen for the next export (transient — the
  // user sets it based on what CMS has already accepted; not persisted).
  const [correctionNums, setCorrectionNums] = useState<Record<string, string>>(
    {}
  );
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsStringLiteral(OASIS_MANAGER_TAB_VALUES)
      .withDefault('incomplete')
      .withOptions({ history: 'replace', clearOnDefault: true })
  );
  const [sortParams, setSortParams] = useQueryStates(oasisSortParsers, {
    history: 'replace',
    clearOnDefault: true,
  });
  // With no sort chosen the server orders by visit date: oldest first on
  // Ready to Export (closest to the deadline), newest first on Exported.
  const activeSort: ColumnSort<OasisSortColumn> = sortParams.sort_by
    ? { column: sortParams.sort_by, direction: sortParams.sort_order }
    : {
        column: 'visit_date',
        direction: activeTab === 'ready_to_export' ? 'asc' : 'desc',
      };
  const [isMarking, setIsMarking] = useState(false);
  // EMR-1864: the action waiting on the External EMR warning, if any.
  const [externalEmrConfirm, setExternalEmrConfirm] = useState<{
    action: ExternalEmrOasisAction;
    count: number;
  } | null>(null);
  const [isPreviewingShp, setIsPreviewingShp] = useState(false);
  const [downloadingShpXmlFor, setDownloadingShpXmlFor] = useState<
    string | null
  >(null);
  // Every export failure — clinic config, CMS (iQIES) rejection, or anything
  // the server hands back — lands here so it renders on a surface that can
  // offer a next step. EMR-2340: a refusal lists each chart's reasons under
  // the server's message.
  const [exportError, setExportError] = useState<ExportError | null>(null);
  const [searchInput, setSearchInput] = useState(
    oasisManagerFilters.patientSearch
  );
  const toast = useToast();
  const navigate = useNavigate();
  const shpEnabled = useShpOasisSubmissionEnabled();
  const canRunShpCheck = useCanRunShpCheck();
  // What an SHP check found when CMS errors kept a chart back from SHP.
  const [shpCheckReadiness, setShpCheckReadiness] = useState<
    Record<string, OasisExportReadiness>
  >({});

  // Debounce patient search — wait 350ms after typing stops before fetching
  useEffect(() => {
    const timer = setTimeout(() => {
      setOASISManagerFilters({ patientSearch: searchInput });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Older stored filters predate the header filters.
  const columnFilters =
    oasisManagerFilters.columnFilters ?? EMPTY_OASIS_MANAGER_COLUMN_FILTERS;
  const setColumnFilter = useCallback(
    (key: keyof OASISManagerColumnFilters, values: string[]) => {
      setSelectedIds(new Set());
      setPage(1);
      setOASISManagerFilters({
        columnFilters: { ...columnFilters, [key]: values },
      });
    },
    [columnFilters, setOASISManagerFilters]
  );
  // The SHP Status column, and so its filter, shows only while SHP is on.
  const shpStatusFilter = shpEnabled
    ? columnFilters.shpStatuses
    : EMPTY_OASIS_MANAGER_COLUMN_FILTERS.shpStatuses;
  const columnFiltered =
    columnFilters.visitTypes.length > 0 ||
    columnFilters.clinicians.length > 0 ||
    columnFilters.clinics.length > 0 ||
    shpStatusFilter.length > 0;
  const keepHeaders = columnFiltered || listWasFiltered;
  const headerFilters = useMemo(() => {
    const options = filterOptions;
    const config = (
      key: keyof OASISManagerColumnFilters,
      choices: { value: string; label: string }[]
    ): HeaderFilterConfig => ({
      options: choices,
      selected: columnFilters[key],
      onChange: values => setColumnFilter(key, values),
    });
    return {
      visitType: config(
        'visitTypes',
        (options?.visit_types ?? []).map(value => ({
          value,
          label: getVisitTypeDisplayName(value),
        }))
      ),
      clinician: config('clinicians', [
        ...(options?.has_unassigned_clinician
          ? [{ value: 'unassigned', label: 'Unassigned' }]
          : []),
        ...(options?.clinicians ?? []),
      ]),
      clinic: config('clinics', [
        ...(options?.has_no_clinic
          ? [{ value: 'none', label: 'No clinic' }]
          : []),
        ...(options?.clinics ?? []),
      ]),
      shpStatus: config('shpStatuses', SHP_STATUS_FILTER_CHOICES),
    };
  }, [filterOptions, columnFilters, setColumnFilter, activeTab]);

  const pageSize = 50;
  // Only the newest request may write the list, so a slow response for an
  // old sort or page cannot overwrite the current one.
  const fetchGenerationRef = useRef(0);

  // Lightweight totals for the tab pills. Incomplete uses the Open OASIS
  // report; Ready/Exported use the manager list with page_size=1.
  const fetchTabCounts = useCallback(async () => {
    if (!hasAccess) return;
    const generation = ++tabCountsGenerationRef.current;
    const listBase = {
      clinic_id: oasisManagerFilters.clinicId || undefined,
      patient_search: oasisManagerFilters.patientSearch || undefined,
      hide_test: oasisManagerFilters.hideTest,
      hide_imported_patients: oasisManagerFilters.hideImportedPatients,
      exclude_pre_takeover: oasisManagerFilters.excludePreTakeover !== false,
      page: 1,
      page_size: 1,
    };
    const [incompleteRes, readyRes, exportedRes] = await Promise.all([
      getOpenOasisReportReportsOpenOasisGet({
        query: {
          page: 1,
          page_size: 25,
          clinic_id: oasisManagerFilters.clinicId || undefined,
          include_test_rows: !oasisManagerFilters.hideTest,
          hide_imported_patients: oasisManagerFilters.hideImportedPatients,
          exclude_pre_takeover:
            oasisManagerFilters.excludePreTakeover !== false,
        },
      }),
      listOasisManagerVisits({
        query: { ...listBase, status: ['completed'] },
      }),
      listOasisManagerVisits({
        query: { ...listBase, status: ['exported'] },
      }),
    ]);
    if (generation !== tabCountsGenerationRef.current) return;
    setTabCounts({
      incomplete:
        !incompleteRes.error && incompleteRes.data
          ? incompleteRes.data.total
          : null,
      ready_to_export:
        !readyRes.error && readyRes.data ? readyRes.data.total : null,
      exported:
        !exportedRes.error && exportedRes.data ? exportedRes.data.total : null,
    });
  }, [
    hasAccess,
    oasisManagerFilters.clinicId,
    oasisManagerFilters.patientSearch,
    oasisManagerFilters.hideTest,
    oasisManagerFilters.hideImportedPatients,
    oasisManagerFilters.excludePreTakeover,
  ]);

  useEffect(() => {
    void fetchTabCounts();
  }, [fetchTabCounts]);

  const fetchVisits = useCallback(async () => {
    if (!hasAccess || activeTab === 'incomplete') return;

    const generation = ++fetchGenerationRef.current;
    setIsLoading(true);
    try {
      const status = STATUS_BY_TAB[activeTab];
      const response = await listOasisManagerVisits({
        query: {
          status: [status],
          patient_search: oasisManagerFilters.patientSearch || undefined,
          clinic_id: oasisManagerFilters.clinicId || undefined,
          hide_test: oasisManagerFilters.hideTest,
          hide_imported_patients: oasisManagerFilters.hideImportedPatients,
          exclude_pre_takeover:
            oasisManagerFilters.excludePreTakeover !== false,
          page,
          page_size: pageSize,
          sort_by: sortParams.sort_by ?? undefined,
          sort_order: sortParams.sort_by ? sortParams.sort_order : undefined,
          visit_type: columnFilters.visitTypes as VisitTypeId[],
          clinician: columnFilters.clinicians,
          clinic: columnFilters.clinics,
          shp_status: shpStatusFilter as ShpStatusFilter[],
        },
      });
      if (generation !== fetchGenerationRef.current) return;
      if (response.data) {
        setListWasFiltered(columnFiltered);
        setVisits(response.data.items);
        setTotal(response.data.total);
        setShpNextRunAt(response.data.shp_next_run_at ?? null);
        const offered = response.data.filter_options ?? null;
        setFilterOptions(offered);
        // A ticked value the tab no longer offers cannot be unticked, yet
        // the server still applies it, so it is dropped.
        const kept = offeredColumnFilters(
          columnFilters,
          offered,
          shpEnabled ? SHP_STATUS_FILTER_CHOICES.map(o => o.value) : null
        );
        if (kept) {
          setPage(1);
          setOASISManagerFilters({ columnFilters: kept });
        }
      }
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return;
      console.error('Failed to load OASIS visits:', err);
      toast.error('Failed to load OASIS visits. Please try again.');
    } finally {
      if (generation === fetchGenerationRef.current) setIsLoading(false);
    }
  }, [
    activeTab,
    hasAccess,
    oasisManagerFilters.patientSearch,
    oasisManagerFilters.clinicId,
    oasisManagerFilters.hideTest,
    oasisManagerFilters.hideImportedPatients,
    oasisManagerFilters.excludePreTakeover,
    columnFilters,
    columnFiltered,
    setOASISManagerFilters,
    shpStatusFilter,
    shpEnabled,
    page,
    sortParams.sort_by,
    sortParams.sort_order,
  ]);

  // A handler that awaits a request would otherwise refetch with the tab,
  // page and sort of the render it was clicked in.
  const latestFetchVisitsRef = useRef(fetchVisits);
  latestFetchVisitsRef.current = fetchVisits;
  const latestFetchTabCountsRef = useRef(fetchTabCounts);
  latestFetchTabCountsRef.current = fetchTabCounts;

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  // EMR-2340: Completed charts CMS will reject can't be selected for export.
  // Only Ready to Export asks: an Exported chart already reached CMS.
  const readinessIds = useMemo(
    () =>
      activeTab === 'ready_to_export'
        ? visits
            // The export skips these anyway; flagging them would only mislead.
            .filter(isCmsExportable)
            .map(v => v.visit_id)
        : [],
    [activeTab, visits]
  );
  const { readiness, refresh: refreshReadiness } =
    useOasisExportReadiness(readinessIds);
  const blockedIds = useMemo(
    () =>
      new Set(
        Object.values(readiness)
          .filter(r => r.status === 'blocked')
          .map(r => r.visit_id)
      ),
    [readiness]
  );
  const selectableVisits = visits.filter(v => !blockedIds.has(v.visit_id));
  // Only SHP test routing names a real patient's chart unsendable, so the
  // hint never shows in production and the stored filter stays the user's.
  const hidesShpTestCharts =
    shpEnabled &&
    oasisManagerFilters.hideTest &&
    visits.some(v => v.shp_unsendable_reason === 'not_test_patient');
  const [errorsDialog, setErrorsDialog] = useState<{
    visit: OasisManagerVisitItem;
    holds: 'export' | 'shp' | 'shp_check';
  } | null>(null);
  const errorsDialogVisit = errorsDialog?.visit ?? null;
  // An SHP hold lists only its record problems; an SHP check lists the CMS
  // errors it found, which a test patient's chart has no export result for.
  const dialogReadiness =
    errorsDialog &&
    (errorsDialog.holds === 'shp'
      ? null
      : errorsDialog.holds === 'shp_check'
        ? (shpCheckReadiness[errorsDialog.visit.visit_id] ??
          readiness[errorsDialog.visit.visit_id])
        : readiness[errorsDialog.visit.visit_id]);

  // A row ticked before its check came back is unticked once it is blocked.
  useEffect(() => {
    if (blockedIds.size === 0) return;
    setSelectedIds(prev => {
      const next = new Set(Array.from(prev).filter(id => !blockedIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [blockedIds]);

  useEffect(() => {
    const visibleIds = new Set(visits.map(visit => visit.visit_id));
    setSelectedIds(prev => {
      const next = new Set(
        Array.from(prev).filter(visitId => visibleIds.has(visitId))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [visits]);

  // Reset selection and page when tab changes. hideTest resets these
  // synchronously in its checkbox handler to avoid a page/fetch race.
  // The export error goes too — it describes a selection that no longer
  // exists, and its Contact Support action would report no visit IDs.
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
    setExportError(null);
    // The old tab's rows would sit under the new tab's columns.
    setVisits([]);
    setTotal(0);
    setListWasFiltered(false);
  }, [activeTab]);

  // A new column sorts ascending; the active column flips direction.
  const handleSort = (column: OasisSortColumn) => {
    const direction =
      activeSort.column === column && activeSort.direction === 'asc'
        ? 'desc'
        : 'asc';
    setSelectedIds(new Set());
    setPage(1);
    void setSortParams({ sort_by: column, sort_order: direction });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableVisits.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableVisits.map(v => v.visit_id)));
    }
  };

  /** CMS rejected the export, or our own scrubber knows it will (EMR-2340).
   * Either names the assessments; support wants those, not whatever is still
   * ticked after the user reacts. A scrubber refusal usually means a chart was
   * reopened and re-completed since the page loaded, so the rows are rechecked. */
  const reportRefusal = (
    refusal: SurfaceableError,
    sentIds: string[]
  ): boolean => {
    const title = REFUSAL_TITLES[refusal.errorCode];
    if (!title) return false;
    setExportError({
      title,
      message: refusal.message,
      visitIds: rejectedVisitIds(refusal.data) ?? sentIds,
      assessments: failedAssessments(refusal.data),
    });
    if (refusal.errorCode === 'oasis_cms_fatal_errors') void refreshReadiness();
    return true;
  };

  /** Where one CMS rejection is fixed, or null (shown as plain text) when the
   * row is not listed, no screen writes the item, or the user cannot edit it. */
  const fixLinkFor = (visitId: string | null, item: string): string | null => {
    const row = visits.find(v => v.visit_id === visitId);
    if (!row || !item) return null;
    switch (cmsItemHome(item)) {
      case 'clinic':
        return canEditClinics && row.clinic_id
          ? clinicSettingsUrl(row.clinic_id, fieldForCmsItem(item))
          : null;
      case 'patient':
        return canEditPatientInfo
          ? patientInfoUrl(row.patient_id, fieldForCmsItem(item))
          : null;
      case 'diagnoses':
        return chartFixUrl(row.patient_id, row.visit_id, { diagnoses: true });
      case 'chart':
        return chartFixUrl(row.patient_id, row.visit_id, { items: [item] });
      case 'none':
        return null;
    }
  };

  /** Where one of Roger's own CMS errors on the dialog's chart is fixed: the
   * chart, or Patient Info / clinic settings for a value the export takes. */
  const readinessFixUrl = (error: OasisValidationError): string | null => {
    if (!errorsDialogVisit) return null;
    const target = chartFixTargetForError(error);
    if (target) {
      return chartFixUrl(
        errorsDialogVisit.patient_id,
        errorsDialogVisit.visit_id,
        target
      );
    }
    for (const field of error.oasis_fields ?? []) {
      const url = fixLinkFor(errorsDialogVisit.visit_id, field);
      if (url) return url;
    }
    return null;
  };

  /** Where a "Not sent to SHP" reason is fixed, or null (plain text) when
   * nothing fixes it or the user cannot edit the screen that does. */
  const shpReasonFixUrl = (row: OasisManagerVisitItem): string | null => {
    switch (row.shp_unsendable_reason) {
      case 'no_provider_number':
      case 'provider_number_not_numeric':
      case 'provider_not_recognized':
        return canEditClinics && row.clinic_id
          ? clinicSettingsUrl(row.clinic_id, 'shp_provider_no')
          : null;
      case 'no_qies_agency_id':
        return canEditClinics && row.clinic_id
          ? clinicSettingsUrl(row.clinic_id, 'cms_qies_agency_id')
          : null;
      case 'test_patient':
      case 'not_test_patient':
        return canEditPatientInfo
          ? patientInfoUrl(row.patient_id, 'patient-test-status-toggle')
          : null;
      default:
        return null;
    }
  };

  const getCorrectionNum = (visitId: string): string =>
    correctionNums[visitId] ?? DEFAULT_CORRECTION_NUM;

  const setCorrectionNum = (visitId: string, value: string) => {
    setCorrectionNums(prev => ({ ...prev, [visitId]: value }));
  };

  /** Downloads the XML the next nightly SHP run would send. Sends nothing. */
  const handleShpPreview = async () => {
    setIsPreviewingShp(true);
    try {
      const response = await previewShpBatch();
      if (response.error) {
        const detail = await getResponseDetailFromBlobOrJson(response.error);
        const message =
          typeof detail === 'string'
            ? detail
            : (detail as { message?: string } | undefined)?.message;
        toast.error(message ?? 'Could not build the SHP batch.');
        return;
      }
      downloadBlobAsFile(
        response.data as BlobPart,
        `shp_batch_${new Date().toISOString().slice(0, 10)}.zip`,
        'application/zip'
      );
      toast.success(
        'Downloaded the XML the next SHP run would send. Nothing was sent.'
      );
    } catch {
      toast.error('Could not build the SHP batch.');
    } finally {
      setIsPreviewingShp(false);
    }
  };

  /** Downloads the XML Roger last sent to SHP for one chart. */
  const handleShpSentXml = async (visit: OasisManagerVisitItem) => {
    setDownloadingShpXmlFor(visit.visit_id);
    try {
      const response = await downloadShpSentXml({
        path: { visit_id: visit.visit_id },
      });
      if (response.error) {
        console.error('Could not download the XML sent to SHP', response.error);
        toast.error('Could not download the sent XML.');
        return;
      }
      downloadResponseAsFile(response, `shp_sent_${visit.visit_date}.xml`);
    } catch (error) {
      console.error('Could not download the XML sent to SHP', error);
      toast.error('Could not download the sent XML.');
    } finally {
      setDownloadingShpXmlFor(null);
    }
  };

  const reportExportError = async (
    error: unknown,
    fallback: string,
    visitIds: string[]
  ): Promise<void> => {
    // Same status gate as getBusinessErrorMessage, but blob-aware (the export
    // is responseType: 'blob', so the sync helper can't read its body). A 5xx
    // body is our failure, not a business message: it can carry internal
    // detail, so it must reach neither the page nor the support request.
    const status = getHttpStatus(error);
    if (status !== undefined && status >= 500) {
      setExportError({
        title: EXPORT_FAILED_TITLE,
        message: fallback,
        detail: `HTTP ${status}`,
        visitIds,
      });
      return;
    }

    const detail = await getResponseDetailFromBlobOrJson(error);
    if (typeof detail === 'string') {
      setExportError({ title: EXPORT_FAILED_TITLE, message: detail, visitIds });
      return;
    }
    const refusal = getSurfaceableError({ error: { detail } });
    if (refusal && reportRefusal(refusal, visitIds)) return;
    if (detail && typeof detail === 'object') {
      const parsed = detail as { message?: string; error_code?: string };
      if (parsed.error_code === 'CLINIC_CONFIG_REQUIRED' && parsed.message) {
        const message = parsed.message;
        setExportError({
          title: 'Clinic configuration required',
          message,
          visitIds,
          body: (
            <>
              {message}. Update the clinic settings in{' '}
              <button
                type='button'
                className='underline text-primary hover:text-primary/80'
                onClick={() => navigate('/admin?section=clinic-info')}
              >
                Admin &rarr; Clinics
              </button>
              . Without access to that,{' '}
              <ContactSupportLink
                subject='OASIS export failed'
                hiddenContext={{
                  error_title: 'Clinic configuration required',
                  error_detail: message,
                  visit_ids: visitIds.join(', '),
                }}
                className='text-primary'
              />
              .
            </>
          ),
        });
        return;
      }
      if (parsed.message) {
        setExportError({
          title: EXPORT_FAILED_TITLE,
          message: parsed.message,
          visitIds,
        });
        return;
      }
    }
    setExportError({
      title: EXPORT_FAILED_TITLE,
      message: getUserFacingApiMessage(error, fallback),
      visitIds,
    });
  };

  const handleExport = async ({ confirmed = false } = {}) => {
    if (selectedIds.size === 0) return;
    // CMS/iQIES XML export excludes test patients (ROG-2900). Mark as Uploaded
    // still works for billing readiness — surface that instead of a silent skip.
    //
    // EMR-1770: the export builder also drops a visit type CMS cannot receive
    // (Skilled Nursing Assessment/Evaluation). Those rows stay listed on the
    // Exported tab so their Mark-as-Uploaded undo remains reachable, and Export
    // ZIP is offered on every tab, so one can be selected here. Partition it out
    // rather than counting it as exported.
    const selectedVisits = visits.filter(v => selectedIds.has(v.visit_id));
    const exportableVisits = selectedVisits.filter(isCmsExportable);
    const exportableIds = exportableVisits.map(v => v.visit_id);
    const skippedTestCount = selectedVisits.filter(
      v => v.is_test_patient
    ).length;
    const skippedNonSubmittableCount = selectedVisits.filter(
      v => !v.is_test_patient && v.is_cms_submittable === false
    ).length;
    if (exportableIds.length === 0) {
      toast.error(
        skippedTestCount > 0
          ? "Test patients can't be exported to CMS (iQIES). Use Mark as Uploaded for billing practice runs."
          : "CMS (iQIES) doesn't accept the selected assessment type."
      );
      return;
    }
    const externalCount = externalEmrOwned(exportableVisits).length;
    if (externalCount > 0 && !confirmed) {
      setExternalEmrConfirm({ action: 'export', count: externalCount });
      return;
    }

    setIsExporting(true);
    setExportError(null);
    try {
      const visitItems = exportableIds.map(id => {
        const correctionNum = getCorrectionNum(id);
        return {
          visit_id: id,
          trans_type_cd: transTypeForCorrectionNum(correctionNum),
          correction_num: correctionNum,
        };
      });

      // EMR-1225: run the same archive past CMS (iQIES) before handing it over.
      // A rejection must not download — the user unchecks the named patients
      // and exports the rest.
      const exportBody = {
        visits: visitItems,
        hide_imported_patients: oasisManagerFilters.hideImportedPatients,
      };
      const precheck = await precheckOasisBatch({
        body: exportBody,
      });
      if (precheck.error) {
        const refusal = getSurfaceableError(precheck);
        if (refusal && reportRefusal(refusal, exportableIds)) return;
        const status = getHttpStatus(precheck);
        if (status !== undefined && status >= 400 && status < 500) {
          // A 4xx is about the request itself (unknown visit, non-OASIS type,
          // missing clinic config). The export would refuse it identically, so
          // surface it rather than downloading a ZIP we know is wrong.
          // Pass the whole result, not `.error` — the helpers below it resolve
          // the body themselves, which is what the other ~68 call sites hand in.
          await reportExportError(
            precheck,
            'Failed to validate OASIS assessments.',
            exportableIds
          );
          return;
        }
        // Anything else — 5xx, a proxy, a dropped connection — is our failure to
        // validate, not CMS's verdict. Adding a second round-trip must not make
        // exporting *less* reliable than before, so fall through and export
        // unvalidated, exactly as an iQIES outage does.
        console.error(
          'OASIS pre-check failed; exporting unvalidated',
          precheck.error
        );
      }

      // EMR-1224: always ship a ZIP, even for a single assessment, so the
      // download shape doesn't change with the number of rows selected.
      const response = await exportOasisBatch({ body: exportBody });
      if (response.error) {
        // Whole result, not `.error` — the helpers resolve the body themselves
        // and the status gate needs the response to see a 5xx.
        await reportExportError(
          response,
          'Failed to export OASIS ZIP.',
          exportableIds
        );
        return;
      }
      if (response.data) {
        downloadBlobAsFile(
          response.data as BlobPart,
          `oasis_batch_${new Date().toISOString().slice(0, 10)}.zip`,
          'application/zip'
        );
        const exported = `Exported ${exportableIds.length} OASIS ${
          exportableIds.length === 1 ? 'assessment' : 'assessments'
        }.`;
        const skipped = [
          skippedTestCount > 0
            ? `${skippedTestCount} test patient(s) (CMS export blocked)`
            : null,
          skippedNonSubmittableCount > 0
            ? `${skippedNonSubmittableCount} assessment(s) CMS doesn't accept`
            : null,
        ].filter(Boolean);
        toast.success(
          skipped.length > 0
            ? `${exported} Skipped ${skipped.join(' and ')}.`
            : exported
        );
      }
    } catch (err) {
      // Network or unexpected errors — API errors are handled via response.error above
      console.error('Error exporting OASIS:', err);
      setExportError({
        title: EXPORT_FAILED_TITLE,
        message: 'Failed to export OASIS. Please try again.',
        // The generic copy above is not enough for support to act on.
        detail: err instanceof Error ? err.message : String(err),
        visitIds: exportableIds,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleMarkExported = async ({ confirmed = false } = {}) => {
    if (selectedIds.size === 0) return;
    const externalCount = externalEmrOwned(
      visits.filter(
        v => selectedIds.has(v.visit_id) && v.is_cms_submittable !== false
      )
    ).length;
    if (externalCount > 0 && !confirmed) {
      setExternalEmrConfirm({ action: 'mark', count: externalCount });
      return;
    }
    setIsMarking(true);
    try {
      const response = await markVisitsExported({
        body: {
          visit_ids: Array.from(selectedIds),
        },
      });
      if (response.error) {
        toast.error(
          getUserFacingApiMessage(
            response,
            'Failed to mark visits as exported.'
          )
        );
        return;
      }
      if (response.data) {
        const { updated_count, skipped_ids } = response.data;
        if (skipped_ids.length > 0) {
          toast.success(
            `Marked ${updated_count} visit(s) as exported. ${skipped_ids.length} skipped.`
          );
        } else {
          toast.success(`Marked ${updated_count} visit(s) as exported.`);
        }
        setSelectedIds(new Set());
        latestFetchVisitsRef.current();
        void latestFetchTabCountsRef.current();
      }
    } catch (err) {
      console.error('Error marking visits as exported:', err);
      toast.error('Failed to mark visits as exported. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const handleUnmarkExported = async () => {
    if (selectedIds.size === 0) return;
    setIsMarking(true);
    try {
      const response = await unmarkVisitsExported({
        body: { visit_ids: Array.from(selectedIds) },
      });
      if (response.error) {
        toast.error('Failed to move visits back to ready.');
        return;
      }
      // Every row on this tab is Exported, so a skipped one is a chart the
      // server kept there because CMS will reject it.
      const skippedIds = new Set(response.data?.skipped_ids ?? []);
      if (skippedIds.size === 0) {
        toast.success('Moved visit(s) back to Ready to Export.');
      } else {
        const names = visits
          .filter(v => skippedIds.has(v.visit_id))
          .map(v => v.patient_name);
        const kept = names.length
          ? names.join(', ')
          : `${skippedIds.size} visit(s)`;
        const charts = skippedIds.size === 1 ? 'that chart' : 'those charts';
        const moved = response.data?.updated_count ?? 0;
        const movedNote =
          moved > 0 ? `Moved ${moved} visit(s) back to Ready to Export. ` : '';
        toast.error(
          `${movedNote}Kept ${kept} on Exported: CMS will reject ${charts}. Reopen to fix.`
        );
      }
      setSelectedIds(new Set());
      latestFetchVisitsRef.current();
      void latestFetchTabCountsRef.current();
    } catch (err) {
      console.error('Error unmarking visits:', err);
      toast.error('Failed to move visits back to ready. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const [reportToolbar, setReportToolbar] = useState<HTMLDivElement | null>(
    null
  );

  if (!hasAccess) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='flex items-center gap-2 text-red-800'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access the OASIS Manager. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Shown in the Spreadsheet's server-sort mode: the grid draws the order the
  // list request returned and hands header clicks to handleSort.
  const serverSort = {
    sorting: [{ id: activeSort.column, desc: activeSort.direction === 'desc' }],
    onSort: (columnId: string) => handleSort(columnId as OasisSortColumn),
  };
  const hiddenColumnIds = [
    ...(activeTab === 'exported'
      ? ['days_remaining']
      : ['exported_at', 'exported_by']),
    ...(shpEnabled ? [] : ['shp_status']),
  ];
  const gridFilters: Record<string, HeaderFilterConfig> = {
    visit_type: headerFilters.visitType,
    clinician: headerFilters.clinician,
    clinic: headerFilters.clinic,
    shp_status: headerFilters.shpStatus,
  };
  const sortable = (
    id: OasisSortColumn,
    header: string,
    size: number,
    cell: (visit: OasisManagerVisitItem) => React.ReactNode
  ): ColumnDef<OasisManagerVisitItem> => ({
    id,
    header,
    size,
    accessorFn: () => null,
    cell: ({ row }) => (
      <div className={OASIS_CELL_CLASS}>{cell(row.original)}</div>
    ),
  });
  const columns: ColumnDef<OasisManagerVisitItem>[] = [
    {
      id: 'select',
      size: 44,
      enableSorting: false,
      meta: { headerAlign: 'center' },
      header: () => (
        <Checkbox
          aria-label='Select all visits'
          checked={
            selectableVisits.length > 0 &&
            selectedIds.size === selectableVisits.length
          }
          disabled={isLoading}
          onCheckedChange={toggleSelectAll}
        />
      ),
      cell: ({ row }) => {
        const visit = row.original;
        const blocked = blockedIds.has(visit.visit_id);
        return (
          <div
            className={`${OASIS_CELL_CLASS} justify-center`}
            title={blocked ? 'Fix CMS errors before exporting' : undefined}
          >
            <Checkbox
              aria-label={`Select ${visit.patient_name}`}
              checked={selectedIds.has(visit.visit_id)}
              disabled={blocked || isLoading}
              onCheckedChange={() => toggleSelect(visit.visit_id)}
            />
          </div>
        );
      },
    },
    sortable('patient_name', 'Patient', 240, visit => {
      const blocked = blockedIds.has(visit.visit_id);
      const errorCount = readiness[visit.visit_id]?.errors?.length ?? 0;
      const errorLabel = `${errorCount} CMS ${errorCount === 1 ? 'error' : 'errors'}`;
      return (
        <>
          <span className='truncate font-medium'>
            <ReportLink to={`/patients/${visit.patient_id}`}>
              {visit.patient_name}
            </ReportLink>
          </span>
          {visit.is_test_patient && <TestPatientBadge className='shrink-0' />}
          {visit.show_imported_badge && (
            <ImportedPatientBadge
              importedAt={visit.imported_at}
              className='shrink-0'
            />
          )}
          {blocked && (
            <button
              type='button'
              aria-label={`${errorLabel} — show why this chart can't be exported`}
              title={errorLabel}
              className='shrink-0 rounded-sm text-destructive hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              onClick={() => setErrorsDialog({ visit, holds: 'export' })}
            >
              <AlertTriangle className='h-4 w-4' />
            </button>
          )}
        </>
      );
    }),
    sortable('visit_type', 'Visit Type', 200, visit => (
      <ReportLink to={`/patients/${visit.patient_id}/visits/${visit.visit_id}`}>
        <Badge variant='outline' className='text-xs whitespace-nowrap'>
          {visit.alias_name ?? getVisitTypeDisplayName(visit.visit_type)}
        </Badge>
      </ReportLink>
    )),
    sortable('visit_date', 'Visit Date', 110, visit =>
      formatDateToMMDDYYYY(visit.visit_date)
    ),
    sortable(
      'exported_at',
      'Exported At',
      170,
      visit => formatDateTimeInAgencyZone(visit.exported_at) || '—'
    ),
    sortable(
      'exported_by',
      'Exported By',
      150,
      visit => visit.exported_by_name || '—'
    ),
    {
      id: 'days_remaining',
      header: 'Days Remaining',
      size: 120,
      cell: ({ row }) => {
        const days = daysUntilDeadline(row.original.visit_date);
        return (
          <div className={OASIS_CELL_CLASS}>
            <span
              className={
                days <= 0
                  ? 'text-destructive font-medium'
                  : days <= 7
                    ? 'text-orange-500 font-medium'
                    : 'text-muted-foreground'
              }
            >
              {days < 0 ? 'Overdue' : days === 0 ? 'Due today' : `${days}d`}
            </span>
          </div>
        );
      },
    },
    {
      id: 'correction_num',
      size: 120,
      header: () => (
        <span className='flex items-center gap-1'>
          Correction #
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  className='inline-flex text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm'
                  aria-label='About correction numbers'
                >
                  <HelpCircle className='h-3.5 w-3.5' />
                </button>
              </TooltipTrigger>
              <TooltipContent side='bottom' className='max-w-xs text-xs'>
                <p>
                  Only change the correction number if you are retransmitting an
                  already accepted OASIS and need to resubmit a corrected one.
                  For the first resubmission use 01, second 02, and so on.
                </p>
                <p className='mt-1.5'>
                  If an OASIS was rejected and you are retransmitting after
                  making corrections, leave the correction number as 00.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      ),
      cell: ({ row }) => (
        <div className={OASIS_CELL_CLASS}>
          <Select
            value={getCorrectionNum(row.original.visit_id)}
            onValueChange={value =>
              setCorrectionNum(row.original.visit_id, value)
            }
          >
            <SelectTrigger
              selectSize='sm'
              className='w-20'
              aria-label='Correction number'
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CORRECTION_NUM_OPTIONS.map(num => (
                <SelectItem key={num} value={num}>
                  {num}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ),
    },
    sortable(
      'clinician',
      'Clinician',
      170,
      visit => visit.assigned_clinician_name ?? '—'
    ),
    sortable('clinic', 'Clinic', 170, visit => visit.clinic_name ?? '—'),
    {
      id: 'shp_status',
      header: 'SHP Status',
      size: 220,
      accessorFn: () => null,
      cell: ({ row }) => {
        const visit = row.original;
        const shp = shpStatus(visit, shpNextRunAt);
        const held = shp.label === SHP_HELD_LABEL;
        const fixAt = shp.detail ? shpReasonFixUrl(visit) : null;
        return (
          <div className='px-2 py-1.5'>
            {held ? (
              <button
                type='button'
                className='whitespace-nowrap text-left text-destructive underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                onClick={() => setErrorsDialog({ visit, holds: 'shp' })}
              >
                {shp.label}
              </button>
            ) : (
              <div className='whitespace-nowrap'>{shp.label}</div>
            )}
            {shp.detail && (
              <div className='text-xs text-muted-foreground'>
                {fixAt ? (
                  <ReportLink to={fixAt}>{shp.detail}</ReportLink>
                ) : (
                  shp.detail
                )}
              </div>
            )}
            {visit.shp_sent_xml_available && (
              <Button
                type='button'
                variant='link'
                size='sm'
                className='h-auto px-0 py-0'
                disabled={downloadingShpXmlFor === visit.visit_id}
                onClick={() => void handleShpSentXml(visit)}
              >
                Download sent XML
              </Button>
            )}
            {canRunShpCheck && (
              <div className='mt-1'>
                <ShpCheckControl
                  visitId={visit.visit_id}
                  lastCheck={visit.shp_last_check ?? null}
                  unsendable={Boolean(visit.shp_unsendable_reason)}
                  onCmsErrors={found => {
                    setShpCheckReadiness(prev => ({
                      ...prev,
                      [visit.visit_id]: found,
                    }));
                    setErrorsDialog({ visit, holds: 'shp_check' });
                  }}
                />
              </div>
            )}
          </div>
        );
      },
    },
    sortable('status', 'Status', 110, visit => (
      <span className='inline-flex items-center gap-1.5'>
        <Badge
          variant={visit.status === 'exported' ? 'default' : 'secondary'}
          className='text-xs'
        >
          {visit.status === 'exported' ? 'Exported' : 'Completed'}
        </Badge>
        <ManagedInExternalEmrMark imported={visit.imported_from_external_emr} />
      </span>
    )),
  ];

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 overflow-auto'>
        <Tabs
          value={activeTab}
          onValueChange={v => {
            const nextTab = v as OASISManagerFilters['activeTab'];
            // Each tab lists other values, so its filters start clear.
            setOASISManagerFilters({
              activeTab: nextTab,
              columnFilters: EMPTY_OASIS_MANAGER_COLUMN_FILTERS,
            });
            void setActiveTab(nextTab);
            // Each tab has its own columns and default order.
            void setSortParams(null);
          }}
          variant='purple'
        >
          {/* Tabs row always keeps the Export/settings host mounted (empty on
              Ready/Exported) so the row height does not collapse. mb-4 below
              the tabs matches Incomplete → OpenOasisReport card spacing
              (Ready used space-y-3 before, a 4px shorter gap). */}
          <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
            <TabsList className='max-sm:h-auto max-sm:flex-wrap'>
              {OASIS_TABS.map(tab => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  <span className='inline-flex items-center gap-x-1'>
                    <span>{tab.label}</span>
                    <TabsCount
                      label={tab.label}
                      value={formatTabCount(tabCounts?.[tab.value])}
                    />
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Incomplete's OpenOasisReport portals Export + settings here. */}
            <div
              ref={setReportToolbar}
              className='flex min-h-8 flex-wrap items-center gap-2'
            />
          </div>

          {activeTab !== 'incomplete' && (
            // Same card chrome as Incomplete's OpenOasisReport filter shell.
            <div className='mb-4 space-y-3 rounded-lg border bg-card p-4'>
              <div className='flex flex-wrap items-end gap-3'>
                <FilterToolbarSearch
                  value={searchInput}
                  onChange={setSearchInput}
                  label='Search'
                  placeholder='Name or MRN...'
                  containerClassName={filterToolbarSearchGrowClassName}
                  className='w-full'
                />
                {/* h-8 + checkboxSize sm match Orders/NOA filter toggles. */}
                <div className='flex h-8 items-center gap-2 self-end'>
                  <Checkbox
                    id='oasisHideTest'
                    checkboxSize='sm'
                    checked={oasisManagerFilters.hideTest}
                    onCheckedChange={(checked: boolean) => {
                      setSelectedIds(new Set());
                      setPage(1);
                      setOASISManagerFilters({ hideTest: !!checked });
                    }}
                  />
                  <label
                    htmlFor='oasisHideTest'
                    className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
                  >
                    Hide test patients
                  </label>
                </div>
                <div className='flex h-8 items-center gap-2 self-end'>
                  <Checkbox
                    id='oasisHideImportedPatients'
                    checkboxSize='sm'
                    checked={oasisManagerFilters.hideImportedPatients}
                    onCheckedChange={(checked: boolean) => {
                      setSelectedIds(new Set());
                      setPage(1);
                      setOASISManagerFilters({
                        hideImportedPatients: !!checked,
                      });
                    }}
                  />
                  <label
                    htmlFor='oasisHideImportedPatients'
                    className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
                  >
                    Filter out imported patients
                  </label>
                </div>
                <ExcludePreTakeoverFilterCheckbox
                  id='oasis-exclude-pre-takeover'
                  className='flex h-8 items-center gap-2 self-end'
                  checked={oasisManagerFilters.excludePreTakeover !== false}
                  onCheckedChange={checked => {
                    setSelectedIds(new Set());
                    setPage(1);
                    setOASISManagerFilters({ excludePreTakeover: checked });
                  }}
                />
                {activeTab === 'ready_to_export' &&
                  shpEnabled &&
                  isSuperUser && (
                    <Button
                      size='sm'
                      variant='outline'
                      className='h-8 shrink-0 self-end'
                      onClick={handleShpPreview}
                      disabled={isPreviewingShp}
                    >
                      {isPreviewingShp ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-1' />
                      ) : (
                        <Download className='h-4 w-4 mr-1' />
                      )}
                      Download next SHP batch
                    </Button>
                  )}
              </div>
            </div>
          )}

          <TabsContent value='incomplete' className='mt-0'>
            <OpenOasisReportWithQueryState
              showBackLink={false}
              toolbarTarget={reportToolbar}
              excludePreTakeover={
                oasisManagerFilters.excludePreTakeover !== false
              }
              onExcludePreTakeoverChange={checked => {
                setOASISManagerFilters({ excludePreTakeover: checked });
              }}
            />
          </TabsContent>

          {activeTab !== 'incomplete' && (
            <>
              {hidesShpTestCharts && (
                <p className='mb-3 text-sm text-muted-foreground'>
                  Test patients are hidden. SHP&apos;s test mode sends only test
                  patients.{' '}
                  <Button
                    type='button'
                    variant='link'
                    size='sm'
                    onClick={() => {
                      setSelectedIds(new Set());
                      setPage(1);
                      setOASISManagerFilters({ hideTest: false });
                    }}
                  >
                    Show test patients
                  </Button>
                </p>
              )}
              {exportError && (
                <DismissibleExportError
                  title={exportError.title}
                  onDismiss={() => setExportError(null)}
                >
                  {exportError.body ?? (
                    <SupportMessage
                      message={exportError.message}
                      subject='OASIS export failed'
                      hiddenContext={{
                        error_title: exportError.title,
                        error_detail: exportError.detail ?? exportError.message,
                        visit_ids: exportError.visitIds.join(', '),
                      }}
                    />
                  )}
                  {exportError.assessments?.some(a => a.reasons.length) && (
                    <ul className='mt-2 space-y-2'>
                      {exportError.assessments.map((assessment, i) => (
                        <li key={`${assessment.patientName ?? 'unknown'}-${i}`}>
                          <span className='font-medium text-foreground'>
                            {assessment.patientName ?? 'Unknown patient'}
                          </span>
                          <ul className='ml-4 list-disc'>
                            {assessment.reasons.map((reason, j) => {
                              const to = fixLinkFor(
                                assessment.visitId,
                                reason.item
                              );
                              return (
                                <li key={j}>
                                  {to ? (
                                    <Link
                                      to={to}
                                      className='underline underline-offset-2 hover:text-foreground'
                                    >
                                      {reason.label}
                                    </Link>
                                  ) : (
                                    reason.label
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  )}
                </DismissibleExportError>
              )}

              {/* Action bar */}
              {selectedIds.size > 0 && (
                <div className='flex items-center gap-3 mb-4 p-3 bg-muted rounded-md'>
                  <span className='text-sm font-medium'>
                    {selectedIds.size} selected
                  </span>
                  <Button
                    size='sm'
                    onClick={() => handleExport()}
                    disabled={isExporting || isLoading}
                  >
                    {isExporting ? (
                      <Loader2 className='h-4 w-4 animate-spin mr-1' />
                    ) : (
                      <Download className='h-4 w-4 mr-1' />
                    )}
                    Export ZIP
                  </Button>
                  {activeTab === 'ready_to_export' && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => handleMarkExported()}
                      disabled={isMarking || isLoading}
                    >
                      {isMarking ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-1' />
                      ) : (
                        <Upload className='h-4 w-4 mr-1' />
                      )}
                      Mark as Uploaded
                    </Button>
                  )}
                  {activeTab === 'exported' && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={handleUnmarkExported}
                      disabled={isMarking || isLoading}
                    >
                      {isMarking ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-1' />
                      ) : (
                        <Undo2 className='h-4 w-4 mr-1' />
                      )}
                      Move to Ready
                    </Button>
                  )}
                </div>
              )}

              <TabsContent value={activeTab} className='mt-0'>
                {/* A filtered list keeps its headers, which hold the filters,
                    through the reload that clears its last value. */}
                {isLoading && visits.length === 0 && !keepHeaders ? (
                  <div className='flex items-center justify-center py-12'>
                    <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                  </div>
                ) : visits.length === 0 && !keepHeaders ? (
                  <div className='flex items-center justify-center py-12 text-sm text-muted-foreground'>
                    {activeTab === 'ready_to_export'
                      ? 'No completed OASIS visits ready to export.'
                      : 'No exported OASIS visits.'}
                  </div>
                ) : (
                  <>
                    <Spreadsheet
                      data={visits}
                      columns={columns}
                      getRowId={visit => visit.visit_id}
                      // Marked as the task list marks rows (EMR-3239).
                      rowClassName={visit =>
                        taskShowsPreTakeOverHatch(
                          { task_date: visit.visit_date },
                          visit.roger_charts_from
                        )
                          ? EXTERNAL_MANAGED_ROW_CLASS
                          : ''
                      }
                      hiddenColumnIds={hiddenColumnIds}
                      serverSort={serverSort}
                      columnFilters={gridFilters}
                      busy={isLoading}
                      emptyText={
                        isLoading ? (
                          <Loader2 className='mx-auto h-5 w-5 animate-spin' />
                        ) : (
                          'No visits match these filters.'
                        )
                      }
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className='flex items-center justify-between mt-4'>
                        <span className='text-sm text-muted-foreground'>
                          Showing {(page - 1) * pageSize + 1}–
                          {Math.min(page * pageSize, total)} of {total}
                        </span>
                        <div className='flex gap-2'>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page <= 1 || isLoading}
                            onClick={() => setPage(p => p - 1)}
                          >
                            Previous
                          </Button>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page >= totalPages || isLoading}
                            onClick={() => setPage(p => p + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
      <OasisExportErrorsDialog
        open={errorsDialogVisit !== null}
        errors={dialogReadiness?.errors ?? []}
        questionTextByQid={dialogReadiness?.question_text_by_qid ?? {}}
        holds={errorsDialog?.holds}
        chartStatus={errorsDialogVisit?.status}
        recordProblems={
          errorsDialog?.holds === 'shp'
            ? errorsDialogVisit?.shp_hold_problems
            : undefined
        }
        hint={
          errorsDialog?.holds === 'export'
            ? 'Click an error to go where it is fixed. The chart can be exported once they are cleared.'
            : 'Click an error to go where it is fixed.'
        }
        onClose={() => {
          // The check's errors are stale once the reviewer has looked.
          if (errorsDialogVisit) {
            const closedId = errorsDialogVisit.visit_id;
            setShpCheckReadiness(prev =>
              Object.fromEntries(
                Object.entries(prev).filter(([id]) => id !== closedId)
              )
            );
          }
          setErrorsDialog(null);
        }}
        actionLabel='Open chart'
        onAction={() => {
          if (!errorsDialogVisit) return;
          navigate(
            `/patients/${errorsDialogVisit.patient_id}/visits/${errorsDialogVisit.visit_id}`
          );
        }}
        canJump={error => readinessFixUrl(error) !== null}
        onJump={error => {
          const url = readinessFixUrl(error);
          if (url) navigate(url);
        }}
      />
      {externalEmrConfirm && (
        <ConfirmationDialog
          open
          {...externalEmrOasisWarning(
            externalEmrConfirm.action,
            externalEmrConfirm.count
          )}
          onCancel={() => setExternalEmrConfirm(null)}
          onAccept={() => {
            const { action } = externalEmrConfirm;
            setExternalEmrConfirm(null);
            if (action === 'export') void handleExport({ confirmed: true });
            else void handleMarkExported({ confirmed: true });
          }}
        />
      )}
    </div>
  );
};

export default OASISManager;
